import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditGoogleClassroom } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  cardId: string
  attachmentId: string
  shareMode: string
}

export function prepareUpsertShareMode(payload: Payload): PreparedAction {
  return {
    name: "LESSON_CARD_GC_CHANGE_SHARE_MODE",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.cardId,
  }).set("embedded", `attributes.attachmentSettings.${payload.attachmentId}`, {
    shareMode: payload.shareMode,
  }).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let models = defaultLessonModels(payload, findFn, userId)
  let newEvent: LessonEditGoogleClassroom = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "google_classroom",

    adverbial_verb: "editing",
    adverbial_object_type: "card",
    adverbial_object_specifier: "gc_share_mode",
    adverbial_object_id: payload.cardId,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }

  return {
    title: "Changed GC attachment sharing setting",
    titleForAnalytics: "Lesson Card - GC Change Share Mode - Schedule",
    description: `Changed GC attachment id ${payload.attachmentId} sharing setting to ${payload.shareMode}"`,
    context: defaultLessonContext(payload, models),
    activity: defaultLessonActivity(payload, models, "created"),
    newAnalyticsEvents: [newEvent],
  }
}
